//display the case allocated to physician

import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import { Table, Typography, Modal, Spin, message } from "antd";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { LoadingOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom"

import PhysicianApi from "../../axios/physician/PhysicianApi";
import SearchAndFilterCases from "../../components/SearchAndFilterCases/SearchAndFilterCases";
import pendingPrescription from "../../redux/pendingPrescription";
import { buildSortableColumns } from '../../utils/tableColumns';
import { dateTimeFormatter } from "../../utils/dateFormatter";
import { createCaseFilterQueryParamMap, createTableQueryParamMap, createTableSortByMap } from "../../utils/createCaseFilterQueryParamMap";
import { CaseStatusEnum, ConsultTypeEnum } from "../../types/enums";
import { TablePreferencesContext } from '../../context/TablePreferencesProvider';
import { CaseFilterMap, CaseQueryParamMap } from "../../constants/CaseFilter"
import "./PendingPrescriptionCases.css";

const { Text } = Typography;

const TABLE_NAME = CaseStatusEnum.PENDING_PRESCRIPTION;
const consultStartDefaultErrorMessage = "Unable to Start Consult. Please try again later!"

const hideFields = Object.freeze([CaseFilterMap.PHYSICIAN_ID, CaseFilterMap.STATUS]);

const TableDataMap = {
  CASE_ID: CaseFilterMap.CASE_ID,
  PATIENT_NAME: `${CaseFilterMap.PATIENT_FIRST_NAME} ${CaseFilterMap.PATIENT_LAST_NAME}`,
  TELEMEDICINE_CATEGORY_ID: CaseFilterMap.TELEMEDICINE_CATEGORY_ID,
  CREATED_DATE: CaseFilterMap.CREATED_DATE,
  UPDATED_DATE: CaseFilterMap.UPDATED_DATE,
}

const TableSortByMap = createTableSortByMap(TableDataMap);

const getTableColumns = (sortOrder, sortBy) => buildSortableColumns(
  [
    { title: "Case", key: TableDataMap.CASE_ID, sortable: false },
    { title: "Patient", key: TableDataMap.PATIENT_NAME },
    { title: "Category", key: TableDataMap.TELEMEDICINE_CATEGORY_ID },
    { title: "Created At", key: TableDataMap.CREATED_DATE },
    { title: "Updated At", key: TableDataMap.UPDATED_DATE },
  ],
  {
    sortOrder,
    sortBy,
    dateKeys: [TableDataMap.CREATED_DATE, TableDataMap.UPDATED_DATE],
  },
);

const defaultPreferences = { currentPage: 1, tablePageSize: 100, sortOrder: 'descend' }

const PendingPrescriptionCases = (props) => {
  const history = useHistory();
  const [showModal, setShowModal] = useState(false)
  const [consultStartConfirmationModal, setConsultStartConfirmationModal ] = useState(false);
  const [caseForConsultationStart, setCaseForConsultationStart] = useState({});
  const [modalButtonLoading, setModalButtonLoading] = useState(false);
  const [searchValue, setSearchValue] = useState(null);
  const [caseListFilter, setCaseListFilter] = useState({});
  const { getTablePreferences, updateTablePreferences } = useContext(TablePreferencesContext);
  const { currentPage, tablePageSize, sortOrder, sortBy } = getTablePreferences(TABLE_NAME, defaultPreferences);

  const {
    caseListLoading,
    pendingPrescriptionCaseList,
    pendingPrescriptionCaseCount,
    pendingPrescriptionCaseNotification,
    statesList,
    telemedicineCategoriesList,
    unassignedCaseNotification,
    actions,
  } = props;

  const columns = useMemo(() => getTableColumns(sortOrder, sortBy), [sortOrder, sortBy]);

  const fetchCases = useCallback(() => {
    const newTablePreferences = {
      currentPage,
      tablePageSize,
      sortOrder,
      sortBy: TableSortByMap[sortBy] || sortBy,
    };
    const tableFilter = createTableQueryParamMap(newTablePreferences);
    const caseFilterMap = createCaseFilterQueryParamMap(caseListFilter, hideFields);
    caseFilterMap[CaseFilterMap.STATUS] = CaseStatusEnum.PENDING_PRESCRIPTION;
    if (searchValue !== null) {
      caseFilterMap[CaseQueryParamMap.SEARCH_TERM] = searchValue;
    }

    actions.getPendingPrescriptionCaseList({ ...tableFilter, ...caseFilterMap });
  }, [actions, caseListFilter, searchValue, currentPage, tablePageSize, sortOrder, sortBy]);

  useEffect(() => {
    fetchCases()
  }, [fetchCases, pendingPrescriptionCaseNotification, unassignedCaseNotification]);

  const handleConsultStartCancellation = (event) => {
    event.stopPropagation();
    setCaseForConsultationStart({});
    setConsultStartConfirmationModal(false);
    message.warn("Sorry, Please start consultation to move to the case details");
  };

  const handleConsultationStartError = (messageString) => {
    setModalButtonLoading(false);
    setConsultStartConfirmationModal(false);
    message.error(messageString);
  };

  const pushRouteCaseDetails = (caseId) => {
    history.push(
      `/dashboard/physician/caseview/${caseId}`
    );
  };

  const handleConsultStartAccept = (event) => {
    event.stopPropagation();
    const data = {
      caseId: caseForConsultationStart.caseId,
      consultationStart: true,
    };
    setModalButtonLoading(true);
    PhysicianApi
      .startConsultation(data)
      .then((res) => {
        if (res.data.success) {
          pushRouteCaseDetails(caseForConsultationStart.caseId);
        } else {
          const errorMessage = res.data?.message || consultStartDefaultErrorMessage;
          handleConsultationStartError(errorMessage);
        }
      })
      .catch((err) => {
        const errorMessage = err?.response?.data?.message || consultStartDefaultErrorMessage;
        handleConsultationStartError(errorMessage);
      });
  };

  const searchCase = (value) => {
    setSearchValue(value);
  };

  const handleOk = (filterData) => {
    setCaseListFilter(filterData);
    setShowModal(false);
  };

  const handleCancel = () => {
    setCaseListFilter({});
    setShowModal(false);
  };

  const handleCancelCross = () => {
    setShowModal(false);
  };

  const handleCaseListClick = (event, record) => {
    event.stopPropagation();
    if ((record.consultationType !== ConsultTypeEnum.STORE_AND_FORWARD) || record.consultationStart) {
      pushRouteCaseDetails(record.caseId);
    } else {
      setCaseForConsultationStart(record);
      setConsultStartConfirmationModal(true);
    }
  };

  const onChangePage = (page) => {
    updateTablePreferences(TABLE_NAME, { currentPage: page })
  };

  const onPageSizeChange = (_, size) => {
    updateTablePreferences(TABLE_NAME, { currentPage: 1, tablePageSize: size })
  };

  const onChangeOrder = (_pagination, _filters, sorter) => {
    updateTablePreferences(TABLE_NAME, {
      sortOrder: sorter.order || 'ascend',
      sortBy: sorter.field,
    });
  }

  const casesForTable = pendingPrescriptionCaseList.map((c) => {
    return {
      key: c.caseId,
      rowKey: c.caseId,
      [TableDataMap.CASE_ID]: c.caseId,
      [TableDataMap.PATIENT_NAME]: c.patientName,
      [TableDataMap.TELEMEDICINE_CATEGORY_ID]: c.category,
      [TableDataMap.CREATED_DATE]: dateTimeFormatter(c.createdDate),
      [TableDataMap.UPDATED_DATE]: c.updatedDate ? dateTimeFormatter(c.updatedDate) : '',
    }
  })

  return (
    <div className="waiting-room">
      <div className="waiting-room-case-list-part-heading">
        <h1>Pending Prescription</h1>
        <SearchAndFilterCases
          searchCase={searchCase}
          showModal={showModal}
          setShowModal={setShowModal}
          handleCancelCross={handleCancelCross}
          handleCancel={handleCancel}
          handleOk={handleOk}
          telemedicines={telemedicineCategoriesList}
          states={statesList}
          hideFields={hideFields}
        />
      </div>

      <div className="waiting-room-no-of-patient-list-data">
        <Spin
          spinning={caseListLoading}
          size="large"
          indicator={<LoadingOutlined spin />}
        >
          <div>
            <Table
              columns={columns}
              dataSource={casesForTable}
              onChange={onChangeOrder}
              pagination={{
                total: pendingPrescriptionCaseCount,
                showTotal: (totalCase) => `Total ${totalCase} Cases`,
                current: currentPage,
                pageSize: tablePageSize,
                pageSizeOptions: ["5", "10", "20", "50", "100"],
                showSizeChanger: true,
                onChange: onChangePage,
                onShowSizeChange: onPageSizeChange,
              }}
              scroll={{ x: 900 }}
              size="large"
              onRow={(record) => {
                return {
                  onClick: (event) => {
                    handleCaseListClick(event, record);
                  },
                };
              }}
            />
          </div>
        </Spin>
        <Modal
          title="Please confirm Consultation Start!"
          okText="Start Consultation"
          closable={false}
          maskClosable={false}
          centered
          open={consultStartConfirmationModal}
          okButtonProps={{
            loading: modalButtonLoading,
          }}
          cancelButtonProps={{
            loading: modalButtonLoading,
          }}
          onOk={(event) => {
            handleConsultStartAccept(event);
          }}
          onCancel={(event) => {
            handleConsultStartCancellation(event);
          }}
        >
          <Text strong>
            You haven't considered this case yet. Please confirm that you are
            starting the consultation for this case
          </Text>
        </Modal>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => {
  const {
    caseListLoading,
    pendingPrescriptionCaseList,
    pendingPrescriptionCaseCount,
  } = state.pendingPrescription;
  const {
    pendingPrescriptionCaseNotification,
    audioVideoCaseNotification,
    unassignedCaseNotification,
  } = state.notifications;
  const { scheduledCaseList, scheduledCaseListLoading } = state.scheduledCases;
  const { statesList, telemedicineCategoriesList } = state.entities;
  return {
    caseListLoading,
    pendingPrescriptionCaseList,
    pendingPrescriptionCaseCount,
    scheduledCaseList,
    scheduledCaseListLoading,
    pendingPrescriptionCaseNotification,
    audioVideoCaseNotification,
    statesList,
    telemedicineCategoriesList,
    unassignedCaseNotification,
  };
};

const mapDispatchToProps = (dispatch) => {
  const { getPendingPrescriptionCaseList } = pendingPrescription.actions;
  return {
    actions: bindActionCreators(
      { getPendingPrescriptionCaseList },
      dispatch
    ),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PendingPrescriptionCases);
